import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SessionNavigator.module.css';
import { logStudyAction } from '../../services/study-log-service';

// "May 3" / "Apr 28" style — terse, fits next to the session name without
// pushing it off the sidebar. Year is omitted when the session is in the
// current year (the common case); shown otherwise so old archives stay
// unambiguous.
function formatSessionDate(input) {
    if (!input) return '';
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const opts = d.getFullYear() === now.getFullYear()
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString(undefined, opts);
}

function sessionTimestamp(s) {
    // Prefer creation_date; fall back to id (DB autoincrement preserves order).
    if (s && s.creation_date) {
        const t = new Date(s.creation_date).getTime();
        if (!isNaN(t)) return t;
    }
    return (s && s.id) || 0;
}

// basePath is intentionally REQUIRED. There is no safe default — different
// layouts route to different roots (/app for TA, /student-app for cs330,
// /math for math agent), and a default that points at one of them turns
// into a silent leak when a future caller forgets the prop. The TA layout,
// student layout, and math layout all pass basePath explicitly today.
const SessionNavigator = ({ sessions, sessionDevices, activeDeviceId, collapsed, onCollapsedChange, basePath, onImportSession }) => {
    if (!basePath) {
        // Fail loudly in dev if someone wires this up without basePath.
        // In production this falls through to a stable behavior: do not
        // navigate at all, rather than redirect to a wrong layout.
        // eslint-disable-next-line no-console
        console.error('SessionNavigator: basePath prop is required (e.g., "/app", "/student-app", "/math").');
    }
    const [expanded, setExpanded] = useState({}); // sessionId → boolean
    const [importOpen, setImportOpen] = useState(false);
    const [importableSessions, setImportableSessions] = useState([]);
    const [importLoading, setImportLoading] = useState(false);
    const navigate = useNavigate();

    const toggleSession = (sessionId) => {
        setExpanded(prev => ({ ...prev, [sessionId]: !prev[sessionId] }));
    };

    const isSessionExpanded = (sessionId) => expanded[sessionId] !== false;

    const handleDeviceClick = (deviceId) => {
        logStudyAction('session_navigate', { session_device_id: deviceId });
        if (!basePath) return; // No-op rather than navigate to wrong layout.
        navigate(`${basePath}/${deviceId}/assessment`);
    };

    // Newest-first ordering. Memoized so React doesn't re-sort on unrelated
    // re-renders (toggle/expand state etc.).
    const sortedSessions = useMemo(() => {
        if (!Array.isArray(sessions)) return [];
        return [...sessions].sort((a, b) => sessionTimestamp(b) - sessionTimestamp(a));
    }, [sessions]);

    const handleOpenImport = async () => {
        setImportOpen(true);
        setImportLoading(true);
        try {
            const res = await fetch('/api/v1/sessions');
            if (!res.ok) return;
            const allSessions = await res.json();
            const mathIds = new Set(sessions.map(s => s.id));
            setImportableSessions(allSessions.filter(s => !mathIds.has(s.id)));
        } catch (_) {}
        setImportLoading(false);
    };

    const handleImportSession = async (sessionId) => {
        try {
            await fetch(`/api/v1/sessions/${sessionId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ category: 'math' }),
            });
            setImportOpen(false);
            if (onImportSession) onImportSession();
        } catch (_) {}
    };

    const handleRemoveSession = async (sessionId, sessionName, e) => {
        e.stopPropagation();
        if (!window.confirm(`Remove "${sessionName}" from Math sessions? It will still be available in the main dashboard.`)) return;
        try {
            await fetch(`/api/v1/sessions/${sessionId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ category: null }),
            });
            if (onImportSession) onImportSession();
        } catch (_) {}
    };

    if (collapsed) {
        return (
            <div className={styles.navCollapsed}>
                <button
                    className={styles.collapseBtn}
                    onClick={() => onCollapsedChange(false)}
                    title="Expand navigator"
                >
                    ☰
                </button>
                {sessions.map(s => (
                    <div key={s.id} className={styles.sessionBadge} title={s.name}>
                        {s.name.charAt(0).toUpperCase()}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className={styles.navigator}>
            <div className={styles.navHeader}>
                <span className={styles.navTitle}>Sessions</span>
                <button
                    className={styles.collapseBtn}
                    onClick={() => onCollapsedChange(true)}
                    title="Collapse navigator"
                >
                    ☰
                </button>
            </div>

            <div className={styles.navList}>
                {sortedSessions.length === 0 && (
                    <div className={styles.loading}>Loading…</div>
                )}
                {sortedSessions.map(session => {
                    const devices = sessionDevices[session.id] || [];
                    const isOpen = isSessionExpanded(session.id);
                    const dateLabel = formatSessionDate(session.creation_date);

                    return (
                        <div key={session.id} className={styles.sessionGroup}>
                            <button
                                className={styles.sessionHeader}
                                onClick={() => toggleSession(session.id)}
                                title={`${session.name}${dateLabel ? ' · ' + dateLabel : ''}`}
                            >
                                <span className={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
                                <span className={styles.sessionName}>
                                    {session.name}
                                </span>
                                {dateLabel && (
                                    <span className={styles.sessionDate}>{dateLabel}</span>
                                )}
                                {onImportSession && (
                                    <span
                                        className={styles.removeSessionBtn}
                                        onClick={(e) => handleRemoveSession(session.id, session.name, e)}
                                        title="Remove from Math sessions"
                                    >
                                        ✕
                                    </span>
                                )}
                            </button>

                            {isOpen && (
                                <div className={styles.deviceList}>
                                    {devices.length === 0 && (
                                        <div className={styles.noDevices}>No devices</div>
                                    )}
                                    {devices.map(device => {
                                        const isActive = String(device.id) === String(activeDeviceId);
                                        return (
                                            <button
                                                key={device.id}
                                                className={`${styles.deviceItem} ${isActive ? styles.deviceItemActive : ''}`}
                                                onClick={() => handleDeviceClick(device.id)}
                                                title={device.name || `Device ${device.id}`}
                                            >
                                                <span className={styles.deviceDot}>·</span>
                                                <span className={styles.deviceName}>
                                                    {device.name || `Device ${device.id}`}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Import session button — only shown when onImportSession is provided */}
            {onImportSession && (
                <div className={styles.importSection}>
                    {!importOpen ? (
                        <button className={styles.importBtn} onClick={handleOpenImport}>
                            + Import Session
                        </button>
                    ) : (
                        <div className={styles.importDropdown}>
                            <div className={styles.importHeader}>
                                <span>Import a session</span>
                                <button className={styles.importCloseBtn} onClick={() => setImportOpen(false)}>✕</button>
                            </div>
                            {importLoading ? (
                                <div className={styles.importLoading}>Loading...</div>
                            ) : importableSessions.length === 0 ? (
                                <div className={styles.importEmpty}>No sessions available to import</div>
                            ) : (
                                <div className={styles.importList}>
                                    {importableSessions.map(s => (
                                        <button
                                            key={s.id}
                                            className={styles.importItem}
                                            onClick={() => handleImportSession(s.id)}
                                        >
                                            {s.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SessionNavigator;
